import React, { useEffect, useState } from "react";
import { Card, Row, Col, Spin, List } from "antd";
import {
  UserAddOutlined,
  TeamOutlined,
  BankOutlined,
  BranchesOutlined,
} from "@ant-design/icons";
import httpInjectorService from "../../services/http-injector.service";
import { useRefresh } from "../../RefreshContext";

const Dashboard = () => {
  const [stats, setStats] = useState({
    newEmployees: 0,
    totalEmployees: 0,
    totalCompanies: 0,
    totalBranches: 0,
  });

  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const { refreshKey } = useRefresh();

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await new Promise((resolve) => setTimeout(resolve, 500));
      try {
        const response = await httpInjectorService.employeeList();
        const employeeData = response.data;

        const totalEmployees = employeeData.length;
        const totalCompanies = new Set(
          employeeData.map((emp) => emp.company_name?.trim().toLowerCase())
        ).size;
        const totalBranches = new Set(
          employeeData.map((emp) => emp.company_branch?.trim().toLowerCase())
        ).size;

        const today = new Date();
        const startOfToday = new Date(today);
        startOfToday.setHours(0, 0, 0, 0);
        const endOfToday = new Date(today);
        endOfToday.setHours(23, 59, 59, 999);

        const todaysEmployees = employeeData.filter((emp) => {
          if (!emp.created_at) return false;
          const createdAt = new Date(emp.created_at);
          return createdAt >= startOfToday && createdAt <= endOfToday;
        });

        const top10Employees = [...employeeData]
          .filter((emp) => emp.created_at)
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, 10);

        setEmployees(top10Employees);
        setStats({
          newEmployees: todaysEmployees.length,
          totalEmployees,
          totalCompanies,
          totalBranches,
        });
      } catch (error) {
        console.error("Failed to fetch data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [refreshKey]);

  const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const date = new Date(dateStr);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear(); // full 4-digit year
  return `${day}-${month}-${year}`;
};

  const iconStyle = { color: "#5167c1", fontSize: "20px", marginRight: "8px" };

  const cardData = [
    {
      title: "New Employees",
      value: stats.newEmployees,
      icon: <UserAddOutlined style={iconStyle} />,
    },
    {
      title: "Total Employees",
      value: stats.totalEmployees,
      icon: <TeamOutlined style={iconStyle} />,
    },
    {
      title: "Total Companies",
      value: stats.totalCompanies,
      icon: <BankOutlined style={iconStyle} />,
    },
    {
      title: "Total Branches",
      value: stats.totalBranches,
      icon: <BranchesOutlined style={iconStyle} />,
    },
  ];

  const cardStyle = {
    borderRadius: "12px",
    margin: "3px",
    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)",
    height: "100%", // ✅ Let card height be auto for responsive
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    padding: "12px",
    transition: "transform 0.3s ease-in-out",
  };

  const displayedEmployees = showAll ? employees : employees.slice(0, 10);

  return (
    <div style={{ padding: "0, 10px" }}>
      <Card
        title="Dashboard Overview"
        bordered
        style={{
          borderRadius: "12px",
          marginTop: 0,
          minHeight: "650px",
          width: "100%",
        }}
      >
        {loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "200px",
            }}
          >
            <Spin spinning={loading} />
          </div>
        ) : (
          <Row gutter={[16, 16]}>
            {/* Stat Cards */}
            <Col xs={24} md={10}>
              <Row gutter={[16, 16]}>
                {cardData.map((card, index) => (
                  <Col key={index} xs={24} sm={12}>
                    <Card bordered style={cardStyle} hoverable>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        {card.icon}
                        <p style={{ margin: 0, fontSize: "14px" }}>
                          {card.title}
                        </p>
                      </div>
                      <div>
                        <p
                          style={{
                            fontSize: "22px",
                            fontWeight: "bold",
                            margin: 0,
                          }}
                        >
                          {card.value}
                        </p>
                      </div>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>

            {/* Employee List */}
            <Col xs={24} md={14}>
              <Card
                title="Employees"
                style={{ ...cardStyle, height: "100%" }}
                extra={
                  employees.length > 10 && (
                    <a onClick={() => setShowAll(!showAll)}>
                      {showAll ? "View Less" : "View All"}
                    </a>
                  )
                }
              >
                <Row
                  style={{
                    fontWeight: "bold",
                    borderBottom: "1px solid #f0f0f0",
                    paddingBottom: 8,
                    fontSize: "14px",
                  }}
                >
                  <Col xs={8}>Name</Col>
                  <Col xs={8}>Work Number</Col>
                  <Col xs={8}>Joining Date</Col>
                </Row>

                <List
                  dataSource={displayedEmployees}
                  renderItem={(item) => (
                    <List.Item
                      style={{
                        paddingLeft: 0,
                        paddingRight: 0,
                        fontSize: "14px",
                      }}
                    >
                      <Row style={{ width: "100%" }}>
                        <Col xs={8}>{item.user_name ?? "Unnamed"}</Col>
                        <Col xs={8}>{item.work_number ?? "N/A"}</Col>
                        <Col xs={8}>{formatDate(item.created_at)}</Col>
                      </Row>
                    </List.Item>
                  )}
                />
              </Card>
            </Col>
          </Row>
        )}
      </Card>
    </div>
  );
};

export default Dashboard;
